<template>
  <div class="kpi" :class="['kpi--' + palette.name]">
    <ViewLoadingMessage extra-class="mt-5" :loading="loading" :size="`sm`"/>
    <WidgetKpi :heading="isActivation ? $gettext('Activations'): $gettext('Check-ins')"
               :heading-icon="`cipEnter`"
               :palette="palette"
               :tooltip="tooltipContent.activation"
               :heading-value="checkinsSummary.total_count|number"
               v-if="!loading">
      <CListGroup flush>
        <CListGroupItem
          class="flex-column align-items-start px-0 pt-0 pb-1 border-bottom-0"
          v-if="checkinsSummary.staying_participants_count">
          <div class="d-flex w-100 justify-content-between">
            <span v-translate>Check-ins during stay</span>
            <span class="font-weight-bold">{{
                checkinsSummary.staying_participants_count|number
              }}</span>
          </div>
        </CListGroupItem>

        <template v-if="isResort">
          <CListGroupItem v-if="ordersSummary && !isMultiEvents"
                          class="flex-column align-items-start px-0 pt-0 pb-1 border-bottom-0">
            <div class="d-flex w-100 justify-content-between">
              <div class="d-flex align-items-start">
                <span>{{ isActivation ? $gettext('CR/Activations') : $gettext('CR/Check-ins') }}</span>
                <PTooltip :content="tooltipContent.activationRate" placement="right" :color="tooltipColor"/>
              </div>
              <span class="font-weight-bold">{{
                  checkinsConversionRate|number
                }}%</span>
            </div>
          </CListGroupItem>
          <CListGroupItem
            class="flex-column align-items-start px-0 pt-0 pb-1 border-bottom-0">
            <div class="d-flex w-100 justify-content-between">
              <span v-translate>Activations attendance today</span>
              <span class="font-weight-bold">{{
                  checkinsSummary.today_staying_count|number
                }}</span>
            </div>
          </CListGroupItem>
        </template>
        <template v-else>
          <CListGroupItem class="flex-column align-items-start px-0 pt-0 pb-1 border-bottom-0"
                          v-if="checkinsSummary">
            <div class="d-flex w-100 justify-content-between">
              <div class="d-flex align-items-start">
                           <span>{{
                               isActivation ? $gettext('Activations/Participants') : $gettext('Check-ins/Participants')
                             }}</span>
                <PTooltip :content="tooltipContent.activationRate" placement="right" :color="tooltipColor"/>
              </div>

              <span class="font-weight-bold">{{ getCheckinsParticipantsPercentage() }}</span>
            </div>
          </CListGroupItem>
          <CListGroupItem class="flex-column align-items-start px-0 pt-0 pb-1 border-bottom-0"
                          v-if="checkinsSummary">
            <div class="d-flex w-100 justify-content-between align-items-center">
              <span v-translate translate-context="checkins.kpi">Estimated participants</span>
              <div class="d-flex align-items-center" v-if="canEditParticipantsCount">
                <template v-if="editMode">
                  <div class="participants-input-wrapper">
                    <input
                      ref="participantsInput"
                      type="number"
                      min="0"
                      v-model.number="participantsCount"
                      class="participants-input"
                      @keyup.enter="saveParticipantsCount"
                      @keyup.esc="cancelEdit"
                    />
                    <div class="participants-input-arrows">
                      <div class="participants-input-arrow" @click="participantsCount++">
                        <CIcon name="cipChevronUp" size="custom-size" :height="10"/>
                      </div>
                      <div class="participants-input-arrow" @click="participantsCount > 0 && participantsCount--">
                        <CIcon name="cipChevronDown" size="custom-size" :height="10"/>
                      </div>
                    </div>
                  </div>
                  <div class="participants-action-btn participants-action-btn--confirm" @click="saveParticipantsCount">
                    <CIcon name="cipCheck" size="custom-size" :height="12"/>
                  </div>
                  <div class="participants-action-btn participants-action-btn--cancel" @click="cancelEdit">
                    <CIcon name="cipClose" size="custom-size" :height="12"/>
                  </div>
                </template>
                <template v-else>
                  <div class="participants-selector">
                    <div class="participants-selector__btn" @click="decrementParticipants">
                      <CIcon name="cipMinus" size="custom-size" :height="12"/>
                    </div>
                    <span class="participants-selector__value" @click="enterEditMode">
                      {{ checkinsSummary.participants_count|number }}
                    </span>
                    <div class="participants-selector__btn" @click="incrementParticipants">
                      <CIcon name="cipPlus" size="custom-size" :height="12"/>
                    </div>
                  </div>
                </template>
              </div>
              <span class="font-weight-bold" v-else>{{ checkinsSummary.participants_count|number }}</span>
            </div>
          </CListGroupItem>
        </template>
      </CListGroup>
    </WidgetKpi>
  </div>
</template>

<script>
import PTooltip from "@/domain/core/components/PTooltip.vue";
import WidgetKpi from "@/views_pica/stats/components/WidgetKpi.vue";
import ViewLoadingMessage from "@/components/ViewLoadingMessage.vue";
import {tooltipColor, tooltipContent} from "@/domain/eventMetric/tooltipContent";
import Palette from "@/domain/core/utils/chart/Palette";
import {mapActions, mapGetters} from "vuex";

export default {
  name: "CheckinsKpi",
  components: {ViewLoadingMessage, WidgetKpi, PTooltip},

  data() {
    return {
      editMode: false,
      participantsCount: 0
    }
  },

  props: {

    isResort: {
      type: Boolean,
      default: false,
    },
    isMultiEvents: {
      type: Boolean,
      default: false,
    },
    isActivation: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('stats', [
      'checkinsSummary',
      'checkinsConversionRate']),
    ...mapGetters('event', ['picaServiceId']),
    ...mapGetters('event', ['isSaas']),
    Palette() {
      return Palette
    },

    tooltipColor() {
      return tooltipColor
    },
    tooltipContent() {
      return tooltipContent
    },
    loading() {
      return Object.keys(this.checkinsSummary).length === 0 || this.checkinsSummary === null
    },
    palette() {
      return {
        name: 'purple',
        color: Palette.purple[0]
      }
    },
    canEditParticipantsCount() {
      return this.hasPerm('stats.checkins.update_participants_count') && this.isSaas
    },
  },
  methods: {
    ...mapActions('stats', ['updateParticipantsCount']),
    getCheckinsParticipantsPercentage() {
      const value = this.checkinsSummary.total_count * 100 / this.checkinsSummary.participants_count
      return (value && isFinite(value) && value > 0) ? `${this.$options.filters.number(value)}%` : 'N/A'
    },
    enterEditMode() {
      this.participantsCount = this.checkinsSummary.participants_count || 0
      this.editMode = true
      this.$nextTick(() => {
        if (this.$refs.participantsInput) {
          this.$refs.participantsInput.focus()
          this.$refs.participantsInput.select()
        }
      })
    },
    cancelEdit() {
      this.editMode = false
    },
    incrementParticipants() {
      const current = (this.checkinsSummary.participants_count || 0) + 1
      this.persistParticipantsCount(current)
    },
    decrementParticipants() {
      const current = this.checkinsSummary.participants_count || 0
      if (current <= 0) return
      this.persistParticipantsCount(current - 1)
    },
    saveParticipantsCount() {
      if (this.participantsCount < 0) return
      this.persistParticipantsCount(this.participantsCount)
      this.editMode = false
    },
    persistParticipantsCount(count) {
      this.updateParticipantsCount({
        picaServiceId: this.picaServiceId,
        params: {participants_count: count}
      })
    }
  }
}
</script>


<style scoped>
.participants-selector {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #faf5ff;
  border: 1px solid #e9d4ff;
  border-radius: 8px;
  padding: 4px;
  gap: 4px;
  min-width: 80px;
}

.participants-selector__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fff;
  border: 1px solid #e9d4ff;
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  box-shadow: 0 0.8px 0.8px rgba(0, 0, 0, 0.04), 0 2.3px 2px rgba(0, 0, 0, 0.03);
  color: #9b00ff;
  transition: background-color 0.15s;
}

.participants-selector__btn:hover {
  background-color: #f3e1ff;
}

.participants-selector__value {
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: #9b00ff;
  cursor: pointer;
  padding: 0 4px;
  user-select: none;
}

.participants-selector__value:hover {
  text-decoration: underline;
}

.participants-input-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #faf5ff;
  border: 1.5px solid #9b00ff;
  border-radius: 8px;
  padding: 4px 12px;
  width: 65px;
}

.participants-input {
  width: 30px;
  border: none;
  background: transparent;
  font-weight: 600;
  font-size: 14px;
  line-height: 18px;
  color: #9b00ff;
  outline: none;
  padding: 0;
  -moz-appearance: textfield;
}

.participants-input::-webkit-outer-spin-button,
.participants-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.participants-input-arrows {
  display: flex;
  flex-direction: column;
  color: #9b00ff;
}

.participants-input-arrow {
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  height: 12px;
  color: #9b00ff;
}

.participants-input-arrow:hover {
  opacity: 0.7;
}

.participants-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 999px;
  cursor: pointer;
  margin-left: 4px;
}

.participants-action-btn--confirm {
  background-color: #4caf50;
  color: #fff;
}

.participants-action-btn--confirm:hover {
  background-color: #43a047;
}

.participants-action-btn--cancel {
  background-color: #edebff;
  color: #4e4d64;
}

.participants-action-btn--cancel:hover {
  background-color: #e1dffb;
}
</style>